import StrategyRefChip from "@/components/strategy/StrategyRefChip";
import StrategyStatusChip from "@/components/strategy/StrategyStatusChip";
import StrategyNameButton from "@/components/strategy/StrategyNameButton";
import GoLiveModal from "@/components/strategy/GoLiveModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { matchesStrategyQuery, noStrategyMatchText, strategyRef, STRATEGY_SEARCH_PLACEHOLDER } from "@/lib/strategyRef";
import { isLive, isPausablePaper, pausePaperStrategy } from "@/lib/strategyPause";
import { goLiveGateBlock, goLiveInitialState, submitGoLive } from "@/lib/goLive";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { RefreshCw, Loader2, Play, Sprout, TrendingUp, TrendingDown, GitBranch, Layers, Search } from "lucide-react";

const fmt = (n, d = 1) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const STATUS = {
  incubating: { label: "INCUBATING", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30",
                hint: "Fewer than 30 closed paper trades yet" },
  qualified:  { label: "QUALIFIED",  cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
                hint: "30+ paper trades and the gate met on its basis — eligible for live" },
  probation:  { label: "PROBATION",  cls: "bg-amber-500/20 text-amber-300 border-amber-500/30",
                hint: "Consistency: win rate 55–70%. Profitability: profit factor 1.2–1.6" },
  repair:     { label: "REPAIR",     cls: "bg-red-500/20 text-red-300 border-red-500/30",
                hint: "Below the probation band, a long loss streak, or the backtest fails its gate — parameter search runs" },
  retired:    { label: "RETIRED",    cls: "bg-zinc-700/40 text-zinc-400 border-zinc-700/30",
                hint: "Superseded by a newer version, or the family was retired by the design lab" },
};

const BASIS = {
  consistency:   { label: "WIN RATE", cls: "border-sky-500/40 text-sky-300",
                   hint: "Qualifies on consistency: 30+ paper trades at 70%+ with positive expectancy" },
  profitability: { label: "P&L",      cls: "border-violet-500/40 text-violet-300",
                   hint: "Qualifies on profitability: 30+ trades, profit factor ≥ 1.6, win rate ≥ 35%, net profit ≥ 2× max drawdown" },
};

const SUITES = { regime_v1: "Regime", expert_v1: "Expert" };
const KIND_ORDER = ["scalp", "intraday", "overnight", "swing"];
// RU-REFSEARCH RS-1: name / label (name and family), symbol, id prefix
const SUITE_FIELDS = {
  name: (s) => `${s.name || ""} ${(s.family || "").replace(/_/g, " ")}`,
  symbol: (s) => s.dsl?.symbol,
  id: (s) => s.id,
};

const QUALIFY_SELECTED_MAX = 50;   // SO-5: the endpoint's bound
const LIVE_NOTE = "live — manage in Strategy Builder";
const rowLabel = (s) => `${s.dsl?.symbol || ""} ${(s.family || "").replace(/_/g, " ")} (${strategyRef(s.id)})`.trim();
const retiredRow = (s) => (s.qualification?.status || "incubating") === "retired" || !!s.superseded_by;

// SO-2 / SO-6: why a pause (enable=false) / resume (enable=true) does not apply to this row; null when it does.
export function pauseSkipReason(s, enable) {
  if (!s) return "no longer listed";
  if (isLive(s)) return LIVE_NOTE;
  if (!isPausablePaper(s)) return "not pausable";
  if (enable && s.deployment?.enabled === true) return "already running";
  if (!enable && s.deployment?.enabled === false) return "already paused";
  return null;
}

// SO-7 / SO-13: rows the Go-Live review never opens the modal for, before any gate read; null otherwise. The gate
// itself is read fresh (SO-12), never from the row's stored live_gate.
export function reviewBlockReason(s) {
  if (isLive(s)) return "already live — manage in Strategy Builder";     // running or stopped
  if (retiredRow(s)) return "retired — not eligible for live";
  return null;
}

function PauseCell({ s, busy, onAsk }) {
  if (isLive(s)) {
    return <div className="text-[10px] txt-muted" data-testid={`suite-live-note-${s.id}`}>{LIVE_NOTE}</div>;
  }
  if (!isPausablePaper(s)) return null;
  const enable = s.deployment?.enabled === false;
  return (
    <button type="button" disabled={busy} onClick={() => onAsk([s], enable)} data-testid={`suite-pause-${s.id}`}
      className="mt-1 text-[10px] font-section px-2 py-0.5 border border-amber-500/40 text-amber-300 hover:text-white disabled:opacity-40">
      {busy ? <Loader2 className="inline w-3 h-3 animate-spin" /> : enable ? "Resume" : "Pause"}
    </button>
  );
}

function Pill({ status }) {
  const m = STATUS[status] || STATUS.incubating;
  return (
    <span title={m.hint} className={`text-[10px] font-section px-2 py-0.5 border ${m.cls}`}>{m.label}</span>
  );
}

function BasisPill({ basis }) {
  const m = BASIS[basis] || BASIS.consistency;
  return (
    <span title={m.hint} className={`text-[10px] font-section px-1.5 py-0.5 border ${m.cls}`}>{m.label}</span>
  );
}

function Row({ s, checked, onCheck, pausing, onAskPause }) {
  const q = s.qualification || {};
  const paper = q.paper || {};
  const bt = q.backtest || {};
  const isLong = (s.dsl?.side || "BUY") === "BUY";
  const gate = s.live_gate || {};
  const basis = gate.basis || q.basis || "consistency";
  const progress = Math.min(100, Math.round(((paper.trades || 0) / (gate.min_paper_trades || 30)) * 100));
  const good = basis === "profitability"
    ? (paper.profit_factor || 0) >= (gate.min_profit_factor || 1.6)
    : (paper.win_rate_pct || 0) >= (gate.min_win_rate_pct || 70);
  return (
    <tr className="border-t border-[var(--border)] hover:bg-white/[0.02]">
      <td className="py-2 pr-2">
        <input type="checkbox" checked={checked} onChange={(e) => onCheck(s.id, e.target.checked)}
          aria-label={`Select ${rowLabel(s)}`} data-testid={`suite-select-${s.id}`} />
      </td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2 flex-wrap">
          {isLong ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
          <StrategyNameButton id={s.id} name={s.name} testid={`suite-strategy-name-${s.id}`} className="font-mono-data text-[var(--text-primary)]">{s.dsl?.symbol}</StrategyNameButton>
          <span className="txt-secondary text-xs">{s.family?.replace(/_/g, " ")}</span>
          <span className="text-[10px] txt-secondary">{SUITES[s.suite] || s.suite}</span>
          <StrategyRefChip id={s.id} testid={`suite-strategy-ref-${s.id}`} />
          <StrategyStatusChip src={s} testid={`suite-strategy-status-${s.id}`} />
          {s.version > 1 && (
            <span className="text-[10px] font-section px-1.5 py-0.5 border border-[var(--border)] txt-secondary">
              <GitBranch className="inline w-3 h-3 mr-1" />v{s.version}
            </span>
          )}
        </div>
      </td>
      <td className="py-2 pr-3 txt-secondary text-xs">{s.dsl?.timeframe}{s.dsl?.timeframe === "1d" ? "" : "m"}</td>
      <td className="py-2 pr-3"><BasisPill basis={basis} /></td>
      <td className="py-2 pr-3"><Pill status={q.status || "incubating"} /></td>
      <td className="py-2 pr-3 font-mono-data text-xs">
        <div>{paper.trades ?? 0} / {gate.min_paper_trades ?? 30}</div>
        <div className="h-1 w-24 bg-white/10 mt-1"><div className="h-1 bg-sky-400" style={{ width: `${progress}%` }} /></div>
      </td>
      <td className={`py-2 pr-3 font-mono-data text-xs ${good && paper.trades ? "text-emerald-400" : ""}`}>
        {paper.trades ? `${fmt(paper.win_rate_pct)}%` : "—"}
      </td>
      <td className={`py-2 pr-3 font-mono-data text-xs ${good && paper.trades ? "text-emerald-400" : ""}`}>
        {paper.trades ? fmt(paper.profit_factor, 2) : "—"}
      </td>
      <td className="py-2 pr-3 font-mono-data text-xs">{paper.trades ? fmt(paper.net_pnl ?? paper.expectancy, 0) : "—"}</td>
      <td className="py-2 pr-3 font-mono-data text-xs">
        {bt.error ? <span className="text-red-400" title={bt.error}>failed</span>
          : bt.trades != null ? `${bt.trades} · ${fmt(bt.win_rate_pct)}% · PF ${fmt(bt.profit_factor, 2)}` : "pending"}
      </td>
      <td className="py-2 txt-secondary text-xs">
        {s.deployment?.enabled ? (s.deployment.mode === "live" ? "live" : "paper") : "off"}
        <PauseCell s={s} busy={pausing} onAsk={onAskPause} />
      </td>
    </tr>
  );
}

export default function RegimeSuite() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [kind, setKind] = useState("all");
  const [suite, setSuite] = useState("all");
  const [hideRetired, setHideRetired] = useState(true);
  const [search, setSearch] = useState("");
  const [instrument, setInstrument] = useState("all");            // SO-3
  const [selected, setSelected] = useState(() => new Set());       // SO-4: ids, visible rows only
  const [pauseAsk, setPauseAsk] = useState(null);                  // SO-2 / SO-6: {ids, enable}
  const [pausingId, setPausingId] = useState("");
  const [qualifying, setQualifying] = useState(false);
  const [review, setReview] = useState(null);                      // SO-7: {queue, index, blocked}
  const [liveModal, setLiveModal] = useState(null);
  const [liveSubmitting, setLiveSubmitting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/strategies/suite");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the strategy suites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  const run = async (path, label) => {
    setBusy(label);
    try {
      const { data } = await api.post(path);
      toast.success(path.endsWith("seed")
        ? `Seeded ${data.seeded} strategies (${data.skipped} already present)`
        : "Qualification pass started — results refresh within a few minutes");
      setTimeout(() => load(true), 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusy("");
    }
  };

  const items = useMemo(() => {
    let rows = data?.items || [];
    if (hideRetired) rows = rows.filter((r) => (r.qualification?.status || "incubating") !== "retired");
    if (kind !== "all") rows = rows.filter((r) => r.kind === kind);
    if (suite !== "all") rows = rows.filter((r) => (r.suite || "regime_v1") === suite);
    if (instrument !== "all") rows = rows.filter((r) => r.dsl?.symbol === instrument);   // SO-3: AND
    rows = rows.filter((r) => matchesStrategyQuery(r, search, SUITE_FIELDS));   // RS-1: AND with the filters
    return rows.sort((a, b) =>
      KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) ||
      (a.dsl?.symbol || "").localeCompare(b.dsl?.symbol || "") ||
      (a.family || "").localeCompare(b.family || "") ||
      (a.dsl?.side || "").localeCompare(b.dsl?.side || ""));
  }, [data, kind, suite, hideRetired, search, instrument]);

  // SO-3: the distinct symbols present in the suite rows
  const instruments = useMemo(
    () => [...new Set((data?.items || []).map((r) => r.dsl?.symbol).filter(Boolean))].sort(),
    [data]);

  // SO-4: keep only selections that are still visible
  useEffect(() => {
    setSelected((prev) => {
      const vis = new Set(items.map((r) => r.id));
      const next = new Set([...prev].filter((id) => vis.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);
  const selectedRows = items.filter((r) => selected.has(r.id));
  const allVisibleSelected = items.length > 0 && items.every((r) => selected.has(r.id));
  const checkRow = (id, on) => setSelected((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });
  const checkAllVisible = (on) => setSelected(on ? new Set(items.map((r) => r.id)) : new Set());

  // SO-2 / SO-6: sequential paper pause / resume through the RU-STRATLINK request; rows read as they are now
  const runPause = async (ids, enable) => {
    const current = new Map((data?.items || []).map((r) => [r.id, r]));
    const done = [], failed = [], skipped = [];
    let conflict = false;
    for (const id of ids) {
      const s = current.get(id);
      const why = pauseSkipReason(s, enable);
      if (why) { skipped.push(`${s ? rowLabel(s) : strategyRef(id)}: ${why}`); continue; }
      setPausingId(id);
      try {
        await pausePaperStrategy(api, id, enable);
        done.push(id);
      } catch (e) {
        failed.push(`${rowLabel(s)}: ${e?.response?.data?.detail || (enable ? "Resume failed" : "Pause failed")}`);
        if (e?.response?.status === 409) conflict = true;
      }
    }
    setPausingId("");
    if (done.length) {
      const doneSet = new Set(done);
      setData((d) => d && ({ ...d, items: d.items.map((r) => (doneSet.has(r.id)
        ? { ...r, deployment: { ...r.deployment, enabled: enable } } : r)) }));
      toast.success(`Paper trading ${enable ? "resumed" : "paused"} for ${done.map((id) => rowLabel(current.get(id))).join(", ")}`);
    }
    failed.forEach((m) => toast.error(m));
    if (skipped.length) toast.error(`Skipped ${skipped.length}: ${skipped.join("; ")}`);
    if (conflict) load(true);       // SL-11: show the strategy's current mode
  };

  // SO-5: re-qualify the selected strategies (caller's own only, server-side)
  const qualifySelected = async () => {
    const ids = selectedRows.map((r) => r.id);
    if (!ids.length) return;
    if (ids.length > QUALIFY_SELECTED_MAX) {
      toast.error(`Select at most ${QUALIFY_SELECTED_MAX} strategies to qualify at once`);
      return;
    }
    setQualifying(true);
    try {
      const { data: res } = await api.post("/strategies/suite/qualify-selected", { ids });
      const results = res?.results || [];
      const ok = results.filter((r) => r.ok);
      const bad = results.filter((r) => !r.ok);
      if (ok.length) toast.success(`Qualified ${ok.length}: ${ok.map((r) => `${strategyRef(r.id)} ${String(r.status || "").toUpperCase()}`).join(", ")}`);
      if (bad.length) toast.error(`Not qualified ${bad.length}: ${bad.map((r) => `${strategyRef(r.id)} (${String(r.error || "failed").replace(/_/g, " ")})`).join(", ")}`);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Qualify selected failed");
    } finally {
      setQualifying(false);
    }
  };

  // SO-7: one strategy at a time through the unchanged GoLiveModal; this page sends no live request itself
  const reviewSeq = useRef(0);
  const reviewAt = async (queue, index) => {
    const seq = ++reviewSeq.current;
    setLiveModal(null);
    if (index >= queue.length) { setReview(null); return; }
    const s = queue[index];
    let blocked = reviewBlockReason(s);
    if (blocked) { setReview({ queue, index, blocked }); return; }
    setReview({ queue, index, blocked: null, checking: true });
    let fresh = null;
    try {   // SO-12: the gate as Strategy Builder computes it, read now
      const { data: g } = await api.get(`/strategies/${s.id}/live-gate`);
      fresh = { ...s, live_gate: g?.live_gate || {} };
      blocked = goLiveGateBlock(fresh);
    } catch (e) {
      blocked = `Could not check the live gate: ${e?.response?.data?.detail || "request failed"}`;
    }
    if (seq !== reviewSeq.current) return;          // moved on meanwhile
    setReview({ queue, index, blocked });
    setLiveModal(blocked ? null : goLiveInitialState(fresh));
  };
  const reviewNext = () => review && reviewAt(review.queue, review.index + 1);
  const endReview = () => { reviewSeq.current += 1; setReview(null); setLiveModal(null); };
  const submitLive = async () => {
    if (!liveModal) return;
    if (liveModal.typed !== "DEPLOY LIVE") { toast.error("Type 'DEPLOY LIVE' exactly to confirm"); return; }
    setLiveSubmitting(true);
    try {
      await submitGoLive(api, liveModal);      // the modal's own submit, shared with Strategy Builder
      toast.warning(`${liveModal.strategy.name} is LIVE on ${liveModal.broker.toUpperCase()}`);
      load(true);
      reviewNext();
    } catch (e) { toast.error(e?.response?.data?.detail || "Live deploy failed"); }
    finally { setLiveSubmitting(false); }
  };

  const counts = suite === "all" ? (data?.counts || {}) : (data?.suites?.[suite]?.counts || {});
  const isAdmin = user?.role === "admin";

  return (
    <AppShell>
      <PageHeader
        overline="Strategies"
        title="Strategy Suites"
        description="Regime and expert strategy suites for MCX commodities and index F&O — forward-tested on paper, auto-repaired and versioned, live only after 30 trades on each strategy's own basis: win-rate consistency, or solid P&L."
        actions={isAdmin && (
          <>
            <button className="flex items-center gap-1.5 text-xs font-section tracking-wider border border-[var(--border)] px-3 py-1.5 txt-muted hover:text-white transition-colors disabled:opacity-40" disabled={!!busy} onClick={() => run("/strategies/suite/seed", "Seed")}>
              {busy === "Seed" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />} Seed missing
            </button>
            <button className="flex items-center gap-1.5 text-xs font-section tracking-wider border border-emerald-500/50 px-3 py-1.5 text-emerald-300 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40" disabled={!!busy} onClick={() => run("/strategies/suite/qualify", "Qualify")}>
              {busy === "Qualify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Qualify now
            </button>
          </>
        )}
      />

      <div className="px-8 py-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.keys(STATUS).map((k) => (
            <div key={k} className="panel p-3">
              <div className="text-[10px] font-section txt-secondary">{STATUS[k].label}</div>
              <div className="font-mono-data text-2xl text-white mt-1">{counts[k] || 0}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Layers className="w-4 h-4 txt-secondary" />
          {["all", ...Object.keys(SUITES)].map((k) => (
            <button key={k} onClick={() => setSuite(k)}
              className={`text-xs font-section px-3 py-1 border ${suite === k ? "border-white text-white" : "border-[var(--border)] txt-secondary"}`}>
              {k === "all" ? "all suites" : SUITES[k]}
            </button>
          ))}
          <span className="w-px h-4 bg-[var(--border)] mx-1" />
          {["all", ...KIND_ORDER].map((k) => (
            <button key={k} onClick={() => setKind(k)}
              className={`text-xs font-section px-3 py-1 border ${kind === k ? "border-white text-white" : "border-[var(--border)] txt-secondary"}`}>
              {k}
            </button>
          ))}
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-1">
            <label className="flex flex-1 sm:flex-none items-center gap-1.5 border border-[var(--border)] px-2 py-1 min-w-0">
              <Search className="w-3.5 h-3.5 txt-secondary shrink-0" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={STRATEGY_SEARCH_PLACEHOLDER} aria-label="Search suite strategies"
                data-testid="suite-search"
                className="bg-transparent text-xs text-white outline-none w-full sm:w-52 min-w-0" />
            </label>
            <select value={instrument} onChange={(e) => setInstrument(e.target.value)}
              aria-label="Filter by instrument" data-testid="suite-instrument"
              className="bg-[var(--bg-page)] border border-[var(--border)] text-xs text-white px-2 py-1 max-w-full">
              <option value="all">All instruments</option>
              {instruments.map((sym) => <option key={sym} value={sym}>{sym}</option>)}
            </select>
          </div>
          <label className="text-xs txt-secondary flex items-center gap-2 ml-2">
            <input type="checkbox" checked={hideRetired} onChange={(e) => setHideRetired(e.target.checked)} /> hide retired
          </label>
          <button className="ml-auto flex items-center gap-1.5 text-xs font-section tracking-wider border border-[var(--border)] px-3 py-1.5 txt-muted hover:text-white transition-colors disabled:opacity-40" onClick={() => load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {selectedRows.length > 0 && (
          <div className="panel p-3 flex flex-wrap items-center gap-2" data-testid="suite-selection-bar">
            <span className="text-xs text-white font-section" data-testid="suite-selection-count">{selectedRows.length} selected</span>
            {isAdmin && (   // SO-10: admin only, like Qualify now
              <button type="button" disabled={qualifying} onClick={qualifySelected} data-testid="suite-qualify-selected"
                className="text-xs font-section px-3 py-1 border border-emerald-500/50 text-emerald-300 hover:text-white disabled:opacity-40">
                {qualifying ? <Loader2 className="inline w-3 h-3 animate-spin mr-1" /> : null}Qualify selected
              </button>
            )}
            <button type="button" disabled={!!pausingId} onClick={() => setPauseAsk({ ids: selectedRows.map((r) => r.id), enable: false })}
              data-testid="suite-pause-selected"
              className="text-xs font-section px-3 py-1 border border-amber-500/40 text-amber-300 hover:text-white disabled:opacity-40">Pause selected</button>
            <button type="button" disabled={!!pausingId} onClick={() => setPauseAsk({ ids: selectedRows.map((r) => r.id), enable: true })}
              data-testid="suite-resume-selected"
              className="text-xs font-section px-3 py-1 border border-amber-500/40 text-amber-300 hover:text-white disabled:opacity-40">Resume selected</button>
            <button type="button" onClick={() => reviewAt(selectedRows, 0)} data-testid="suite-review-live"
              title="Opens the Go-Live confirmation for each selected strategy, one at a time"
              className="text-xs font-section px-3 py-1 border border-red-500/40 text-red-300 hover:text-white">Review for live</button>
            <button type="button" onClick={() => checkAllVisible(false)} data-testid="suite-selection-clear"
              className="text-xs font-section px-3 py-1 border border-[var(--border)] txt-secondary hover:text-white">Clear</button>
          </div>
        )}

        <div className="panel p-4 overflow-x-auto">
          {loading && !data ? (
            <div className="flex items-center gap-2 txt-secondary text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading suites…</div>
          ) : items.length === 0 && search.trim() ? (
            <div className="txt-secondary text-sm" data-testid="suite-search-empty">{noStrategyMatchText(search)}</div>
          ) : items.length === 0 && instrument !== "all" ? (
            <div className="txt-secondary text-sm" data-testid="suite-instrument-empty">
              No suite strategies for {instrument} with the current filters.
            </div>
          ) : items.length === 0 ? (
            <div className="txt-secondary text-sm">
              No suite strategies yet. {isAdmin ? "Use Seed missing to create them." : "Ask an admin to seed the suites."}
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-[10px] font-section txt-secondary">
                <tr>
                  <th className="pb-2 pr-2">
                    <input type="checkbox" checked={allVisibleSelected} onChange={(e) => checkAllVisible(e.target.checked)}
                      aria-label="Select all visible strategies" data-testid="suite-select-all" />
                  </th>
                  <th className="pb-2 pr-3">Strategy</th>
                  <th className="pb-2 pr-3">TF</th>
                  <th className="pb-2 pr-3">Basis</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3">Paper trades</th>
                  <th className="pb-2 pr-3">Win rate</th>
                  <th className="pb-2 pr-3">PF</th>
                  <th className="pb-2 pr-3">Net ₹</th>
                  <th className="pb-2 pr-3">Backtest (trades · win · PF)</th>
                  <th className="pb-2">Mode</th>
                </tr>
              </thead>
              <tbody>{items.map((s) => (
                <Row key={s.id} s={s} checked={selected.has(s.id)} onCheck={checkRow}
                     pausing={pausingId === s.id}
                     onAskPause={(rows, enable) => setPauseAsk({ ids: rows.map((r) => r.id), enable })} />
              ))}</tbody>
            </table>
          )}
        </div>

        <AlertDialog open={!!pauseAsk} onOpenChange={(o) => { if (!o) setPauseAsk(null); }}>
          <AlertDialogContent data-testid="suite-pause-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pauseAsk?.ids.length === 1
                  ? `${pauseAsk?.enable ? "Resume" : "Pause"} paper trading for ${(() => {
                      const s = (data?.items || []).find((r) => r.id === pauseAsk.ids[0]);
                      return s ? rowLabel(s) : strategyRef(pauseAsk.ids[0]);
                    })()}?`
                  : `${pauseAsk?.enable ? "Resume" : "Pause"} paper trading for ${pauseAsk?.ids.length} selected strategies?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pauseAsk?.enable
                  ? "They can open new paper positions."
                  : "Open positions are not closed; exits keep running."}
                {" "}Paper only: live strategies and strategies that cannot be paused here are skipped.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="suite-pause-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="suite-pause-confirm"
                onClick={() => { const a = pauseAsk; setPauseAsk(null); if (a) runPause(a.ids, a.enable); }}>
                {pauseAsk?.enable ? "Resume" : "Pause"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {review && (
          <div className="fixed bottom-0 inset-x-0 z-[60] panel border-t border-red-500/40 p-3 flex flex-wrap items-center gap-2"
               data-testid="suite-review-bar">
            <span className="text-xs text-white font-section" data-testid="suite-review-progress">
              Go-Live review {review.index + 1} of {review.queue.length}: {rowLabel(review.queue[review.index])}
            </span>
            {review.checking && (
              <span className="text-xs txt-secondary" data-testid="suite-review-checking">
                <Loader2 className="inline w-3 h-3 animate-spin mr-1" />checking the live gate…
              </span>
            )}
            {review.blocked && (
              <span className="text-xs text-red-300" data-testid="suite-review-blocked">{review.blocked}</span>
            )}
            <button type="button" onClick={reviewNext} data-testid="suite-review-next"
              className="text-xs font-section px-3 py-1 border border-[var(--border)] txt-secondary hover:text-white">
              {review.blocked ? "Next" : "Skip"}
            </button>
            <button type="button" onClick={endReview} data-testid="suite-review-end"
              className="text-xs font-section px-3 py-1 border border-[var(--border)] txt-secondary hover:text-white">End review</button>
          </div>
        )}
        <GoLiveModal state={liveModal} onClose={reviewNext} onChange={setLiveModal}
                     onSubmit={submitLive} submitting={liveSubmitting} />

        <p className="txt-secondary text-xs max-w-3xl">
          Regime suite: session-regime scalps, intraday and overnight carry on the six commodities; opening-range
          break, closing drive and option spreads, strangles and buys on the indices. Expert suite: daily swing
          pull-backs, breakouts and RSI(2) reversion on indices and commodities; MCX VWAP reversion and opening-range
          break; credit spreads, iron condors, short straddles, trend option buys, long straddles and swing debit
          spreads on the indices. Trend, breakout, swing, carry and option-buying families qualify on P&amp;L
          (profit factor, drawdown); scalps, fades, reversion and option selling qualify on win rate. The qualifier
          refreshes backtests and paper records every six hours; a strategy in repair is re-tuned by backtest on its
          basis and, when a candidate is clearly better, seeded as the next version while the old one is retired with
          its history intact.
        </p>
      </div>
    </AppShell>
  );
}
